import type { PortalAssetLifecycle, PortalAssetMediaType, PortalAssetReviewStatus } from '../../types/portalAsset.js';
import { localDayStart, narrowMode, searchState } from './sortSearchView.js';
import type { SortOptionId } from './sortOptions.js';

// THE ACCOUNT PAGE'S "Assets" TAB: FORM STATE -> QUERY (Run B). PURE.
//
// The same filter set as the staff cross-account page (category, category type, status, photos/videos, processing
// state, uploaded from/to), plus sort and search. Brand / group / manager / club are absent here: the account IS the scope.
//
// ⚠️ Same rules as staffAllView.ts, stated once there: a category and a category type exclude each other (two
// array-contains clauses cannot share a query); a date range needs a date sort (chooseSort clears it); and a running
// search sends the SCOPE ALONE — every other value stays in the form (disabled on screen) and is simply not sent.
// ⚠️ A tag is not offered here, so there is no tag-vs-others rule to enforce.

export type AccountAssetsForm = {
  status: PortalAssetReviewStatus | '';
  mediaType: PortalAssetMediaType | '';
  lifecycle: PortalAssetLifecycle | '';
  /** One of THIS account's brand's categories. ⚠️ Excludes `categoryKind`. */
  categoryId: string;
  /** One category TYPE (e.g. team). ⚠️ Excludes `categoryId`. */
  categoryKind: string;
  /** YYYY-MM-DD from a date input, or ''. */
  from: string;
  to: string;
  sort: SortOptionId;
  /** The raw box contents; whether it is a usable query is searchState's call. */
  search: string;
  /** STAFF ONLY: "Not reviewed by Kyck" — a scope-alone path (see sortSearchView.narrowMode). Always false on the club's own page. */
  kyckUnreviewed: boolean;
};

export const EMPTY_ACCOUNT_ASSETS_FORM: AccountAssetsForm = {
  status: '',
  mediaType: '',
  lifecycle: '',
  categoryId: '',
  categoryKind: '',
  from: '',
  to: '',
  sort: 'newest',
  search: '',
  kyckUnreviewed: false,
};

export function chooseAccountCategory(form: AccountAssetsForm, categoryId: string): AccountAssetsForm {
  return { ...form, categoryId, categoryKind: categoryId ? '' : form.categoryKind };
}

export function chooseAccountCategoryKind(form: AccountAssetsForm, categoryKind: string): AccountAssetsForm {
  return { ...form, categoryKind, categoryId: categoryKind ? '' : form.categoryId };
}

/** Only defined values; an unreadable date is dropped rather than sent. */
export type AccountAssetsParams = {
  /** Absent only on the CLUB's page, where "all my locations" is the whole scope. */
  accountId?: string;
  /** STAFF ONLY. */
  kyckUnreviewed?: true;
  status?: PortalAssetReviewStatus;
  mediaType?: PortalAssetMediaType;
  lifecycle?: PortalAssetLifecycle;
  categoryId?: string;
  categoryKind?: string;
  sort?: SortOptionId;
  search?: string;
  from?: string;
  to?: string;
};

export function accountAssetsParams(form: AccountAssetsForm, accountId: string): AccountAssetsParams {
  const s = searchState(form.search);
  if (s.active) return { accountId, search: s.query };
  // ⚠️ "Not reviewed by Kyck" sends the SCOPE ALONE, like a search: every other value stays in the form and is not sent.
  if (narrowMode(form) === 'unreviewed') return { accountId, kyckUnreviewed: true };
  const p: AccountAssetsParams = { accountId };
  if (form.status) p.status = form.status;
  if (form.mediaType) p.mediaType = form.mediaType;
  if (form.lifecycle) p.lifecycle = form.lifecycle;
  if (form.categoryId) p.categoryId = form.categoryId;
  else if (form.categoryKind) p.categoryKind = form.categoryKind;
  if (form.sort !== 'newest') p.sort = form.sort;
  const from = localDayStart(form.from);
  const to = localDayStart(form.to, 1);
  if (from) p.from = from;
  if (to) p.to = to;
  return p;
}

/** True when the chosen range is upside down — the server would 400, the page says so instead. */
export function accountRangeIsBackwards(form: AccountAssetsForm): boolean {
  const from = localDayStart(form.from);
  const to = localDayStart(form.to, 1);
  return from !== undefined && to !== undefined && Date.parse(from) >= Date.parse(to);
}

/** Filters that narrow the list. ⚠️ The sort is not a filter, so choosing one never shows "Clear filters". */
export function hasAnyAccountFilter(form: AccountAssetsForm): boolean {
  const { accountId: _a, sort: _s, ...rest } = accountAssetsParams(form, '');
  return Object.keys(rest).length > 0;
}

/**
 * THE CLUB'S OWN PAGE (Run C): the same form and query rules, with the account optional and the Kyck-only filter made
 * impossible. ⚠️ `kyckUnreviewed` is stripped here rather than trusted to be false: the client list schema is strict and would
 * 400 it, and "a club's request cannot carry a Kyck-review filter" should not rest on a page remembering to hide a box.
 */
export function clientAssetsParams(form: AccountAssetsForm, accountId: string | undefined): AccountAssetsParams {
  const p = accountAssetsParams({ ...form, kyckUnreviewed: false }, accountId ?? '');
  if (accountId === undefined) delete p.accountId;
  return p;
}
